import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconEndAdornment } from 'components/Button';
import { Card } from 'components/Card';
import { OrderedList } from 'components/Lists';
import { Heading, Paragraph } from 'components/Typography';
import {
  CustomStructuredActivityCtx,
  withCustomStructuredActivityContext,
} from 'domains/customStructuredActivities/context';
import { structuredActivityStepId } from 'domains/structuredActivities/steps';
import { LinkedParagraph } from 'components/Typography/LinkedParagraph';
import { IconButton } from 'components/IconButton';
import { Accordion, AccordionItem } from '@expressable/syntax-ds';

const PracticeOverviewContent: React.FC = () => {
  const { structuredActivityContent: content, onNavigateToStep, previewMode } = useContext(CustomStructuredActivityCtx);
  const { t } = useTranslation();

  const handleContinue = () => onNavigateToStep(structuredActivityStepId.submitActivity, true);

  const { whatYoullNeed, whatClientNeedForActivity, activityGoals, practiceOverview } = content;

  const {
    step1Description,
    step1Title,
    step2Description,
    step2Title,
    step3Description,
    step3Title,
    step4Title,
    step4Description,
    step5Title,
    step5Description,
    whatToDoIfThisFeelsTough,
    whatToLookForDuringThisActivity,
    associatedMaterials,
  } = content;

  const material = useMemo(() => {
    if (associatedMaterials && associatedMaterials[0]?.fields.file) {
      return associatedMaterials[0].fields.file;
    }

    return null;
  }, [associatedMaterials]);

  return (
    <>
      <section>
        <Heading level="h4" useDefaultMargins>
          {t('structuredActivity.practiceOverview.title')}
        </Heading>
        <Paragraph>
          <LinkedParagraph>{practiceOverview}</LinkedParagraph>
        </Paragraph>
      </section>

      {material && (
        <section className="flex gap-16 items-center">
          <IconButton
            name="Download"
            title="download asset"
            variant="secondary"
            size="lg"
            as="a"
            href={material.url}
            target="_blank"
            className="shrink-0"
          />

          <Button variant="link" as="a" href={material.url} target="_blank">
            {t('structuredActivity.practiceOverview.downloadAsset', {
              title: material.fileName,
            })}
          </Button>
        </section>
      )}

      <section>
        <Heading level="h4" useDefaultMargins>
          {t('structuredActivity.practiceOverview.goals')}
        </Heading>
        {activityGoals && <LinkedParagraph>{activityGoals}</LinkedParagraph>}
      </section>

      {(whatYoullNeed || whatClientNeedForActivity) && (
        <section>
          <Heading level="h4" useDefaultMargins>
            {t('structuredActivity.practiceOverview.whatYoullNeed')}
          </Heading>
          <LinkedParagraph>{whatYoullNeed || whatClientNeedForActivity}</LinkedParagraph>
        </section>
      )}

      <section>
        <Heading level="h4" useDefaultMargins>
          {t('structuredActivity.practiceOverview.guidelines')}
        </Heading>

        <OrderedList>
          {step1Title && step1Description && (
            <OrderedList.Item number={1}>
              <Heading level="h5" className="mb-8 font-semibold">
                {step1Title}
              </Heading>
              <LinkedParagraph>{step1Description}</LinkedParagraph>
            </OrderedList.Item>
          )}
          {step2Title && step2Description && (
            <OrderedList.Item number={2}>
              <Heading level="h5" className="mb-8 font-semibold">
                {step2Title}
              </Heading>
              <LinkedParagraph>{step2Description}</LinkedParagraph>
            </OrderedList.Item>
          )}
          {step3Title && step3Description && (
            <OrderedList.Item number={3}>
              <Heading level="h5" className="mb-8 font-semibold">
                {step3Title}
              </Heading>
              <LinkedParagraph>{step3Description}</LinkedParagraph>
            </OrderedList.Item>
          )}
          {step4Title && step4Description && (
            <OrderedList.Item number={4}>
              <Heading level="h5" className="mb-8 font-semibold">
                {step4Title}
              </Heading>
              <LinkedParagraph>{step4Description}</LinkedParagraph>
            </OrderedList.Item>
          )}
          {step5Title && step5Description && (
            <OrderedList.Item number={5}>
              <Heading level="h5" className="mb-8 font-semibold">
                {step5Title}
              </Heading>
              <LinkedParagraph>{step5Description}</LinkedParagraph>
            </OrderedList.Item>
          )}
        </OrderedList>
      </section>

      <Button
        className="sm-d:w-full"
        size="lg"
        onClick={handleContinue}
        endAdornment={<IconEndAdornment name="AngleRight" />}
        disabled={previewMode}
      >
        {t('structuredActivity.practiceOverview.submit')}
      </Button>

      {(whatToDoIfThisFeelsTough || whatToLookForDuringThisActivity) && (
        <Accordion title={t('structuredActivity.practiceOverview.supportTitle')}>
          {whatToLookForDuringThisActivity && (
            <AccordionItem title={t('structuredActivity.practiceOverview.whatToLookFor')}>
              <LinkedParagraph>{whatToLookForDuringThisActivity}</LinkedParagraph>
            </AccordionItem>
          )}

          {whatToDoIfThisFeelsTough && (
            <AccordionItem title={t('structuredActivity.practiceOverview.whatIfItFeelstough')}>
              <LinkedParagraph>{whatToDoIfThisFeelsTough}</LinkedParagraph>
            </AccordionItem>
          )}
        </Accordion>
      )}

      <Card className="bg-yellow-300 w-full">
        <Heading level="h4" className="mb-40 sm-d:mb-32">
          {t('dailyRoutine.practiceOverview.feedbackCallout.text')}
        </Heading>

        <Button
          className="sm-d:w-full"
          size="lg"
          onClick={handleContinue}
          endAdornment={<IconEndAdornment name="AngleRight" />}
          disabled={previewMode}
        >
          {t('dailyRoutine.practiceOverview.feedbackCallout.button')}
        </Button>
      </Card>
    </>
  );
};

export default withCustomStructuredActivityContext(PracticeOverviewContent, structuredActivityStepId.overview);
